// SPA shim — drops the next/redirect-error import (server-only) and the
// "use server" directive. handleErrorServer now returns the same shape the
// legacy callers expect; client code continues to interact via .isServerError.
import * as Sentry from "@sentry/react";
import { AuthError } from "./AuthError";

/**
 * Sentinel thrown by GuestsProvider.addGuest when a create hits the capacity
 * decision: the 3-option popup is now open in the provider, so CreateGuestBody
 * must close itself WITHOUT showing an inline error. addGuest cannot return a
 * null/sentinel value because GuestList.onCreation reads the returned guest
 * synchronously — a throw is the only way to short-circuit that path.
 */
export const CAPACITY_DECISION_PENDING = "CAPACITY_DECISION_PENDING";

/**
 * Capacity snapshot returned with `capacityDecisionRequired` (create) and on
 * `approvalBlockedByRules` failures (accept / waitlist promotion). Drives the
 * 3-option capacity popup: `globalWaitlistEnabled` shows/hides the waitlist
 * button; `waitlistHasRoom=false` switches the body copy to "waitlist is full".
 */
export type CapacityContext = {
  globalWaitlistEnabled: boolean;
  waitlistHasRoom: boolean;
  globalGuestLimit: number | null;
  globalWaitlistLimit: number | null;
  currentCount: number;
  waitlistedCount: number;
};

export type GuestUpdateFailure = {
  guestId: string;
  errorCode: string;
  message?: string;
  /** Present on `approvalBlockedByRules` failures (capacity popup). */
  capacityContext?: CapacityContext;
};

export type ServerError = {
  message: string;
  isServerError: true;
  /** HTTP status, where a handler keeps it (fetchDetailedSubscriptions — its
   *  query must not retry a 401/403/429, and a @RolesAllowed 403 has no body).
   *  Named `httpStatus`, not `status`: a DTO with its own `status` (e.g.
   *  FloorplanDetectionDTO) intersected with ServerError would collapse to
   *  `never`. */
  httpStatus?: number;
  /** Raw human-readable backend message (English) when it differs from the
   *  resolved i18n code in `message` — e.g. "expectedCapacity: Expected
   *  capacity must be at most 1000000" alongside code `validationError`.
   *  Surfaced by `translateBackendError` for generic catch-all codes. */
  detail?: string;
  retryAfter?: number;
  violations?: Array<{ fieldKey: string; code: string }>;
  violationsTruncated?: boolean;
  /** Per-guest failures from the batch-update partial-failure response
   *  ({@code batchUpdatePartialFailure}). */
  failures?: GuestUpdateFailure[];
  /** Top-level capacity snapshot from the create flow's 409
   *  ({@code capacityDecisionRequired}). */
  capacityContext?: CapacityContext;
  /** Offending contact ids from the add-from-CRM 404 ({@code contactNotFound})
   *  — deleted/foreign ids the caller should drop from its selection. */
  missingContactIds?: string[];
  /** Addresses on the platform suppression list that refused a test mail
   *  ({@code recipientSuppressed}, Kanzlei P5) — as the caller typed them. */
  suppressedRecipients?: string[];
  /** Candidate guests from the speaker-promotion 409
   *  ({@code multipleGuestMatches}) — the FE picker retries the promote call
   *  with the chosen row's guestId as {@code attachGuestId}. */
  candidates?: GuestMatchCandidate[];
  /** The client's own checks refused the request before it was sent — no
   *  server saw it (set by `submitBackgroundJob` for a throw ahead of the
   *  wire). Absent for anything that may have reached a server. */
  local?: true;
};

/**
 * What a failed save hands to its render site.
 *
 * NOT a pre-translated sentence: providers have no translator, and the guest
 * batch 400 carries no top-level `message` at all — the only human-readable
 * reason lives per-row in {@link GuestUpdateFailure.message}. So the structure
 * travels and the component translates it with `describeBatchUpdateError`.
 * The plain-string arm is the legacy shape (a bare error code) and stays
 * supported; `PUnsavedChanges` already models its own save error this way.
 */
export type SaveErrorPayload = {
  message?: string;
  detail?: string;
  failures?: GuestUpdateFailure[];
};

/** One row of the promote endpoint's multi-match 409 payload (identity
 *  fields only — mirrors EventSpeakerPromotionService.GuestMatchCandidate). */
export type GuestMatchCandidate = {
  guestId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

/**
 * Narrows a handler return (`T | ServerError`) to a {@link ServerError}.
 * Handlers catch and return this sentinel rather than throwing, so callers
 * must check it before treating the value as success — forgetting to is a
 * recurring silent-failure bug class.
 */
export function isServerErr(value: unknown): value is ServerError {
  return (
    !!value &&
    typeof value === "object" &&
    "isServerError" in value &&
    (value as { isServerError?: unknown }).isServerError === true
  );
}

/**
 * The rich backend-error fields (everything on {@link ServerError} beyond the
 * message) that must survive when an error is re-wrapped into a plain thrown
 * Error. `throwServerError` and `handleError` copy them forward and
 * `handleErrorServer` reads them back, so a ServerError → throw →
 * ServerError round-trip loses nothing.
 */
type RichErrorFields = Omit<ServerError, "message" | "isServerError">;

/**
 * Runtime-guarded, field-by-field copy of the rich fields from `from` onto
 * `to` — the single place that knows how each field is validated. Sources are
 * often only nominally typed (axios `response.data`, a caught Error's own
 * properties), so every field is re-checked at runtime; a field missing or
 * malformed on `from` never clobbers an existing value on `to`.
 */
function copyRichFields(from: RichErrorFields, to: RichErrorFields): void {
  if (typeof from.httpStatus === "number") to.httpStatus = from.httpStatus;
  if (typeof from.detail === "string" && from.detail.length > 0)
    to.detail = from.detail;
  if (typeof from.retryAfter === "number" && from.retryAfter > 0)
    to.retryAfter = from.retryAfter;
  if (Array.isArray(from.violations)) to.violations = from.violations;
  if (typeof from.violationsTruncated === "boolean")
    to.violationsTruncated = from.violationsTruncated;
  if (Array.isArray(from.failures)) to.failures = from.failures;
  if (from.capacityContext && typeof from.capacityContext === "object")
    to.capacityContext = from.capacityContext;
  if (Array.isArray(from.missingContactIds))
    to.missingContactIds = (from.missingContactIds as unknown[]).filter(
      (id): id is string => typeof id === "string",
    );
  if (Array.isArray(from.suppressedRecipients))
    to.suppressedRecipients = (from.suppressedRecipients as unknown[]).filter(
      (a): a is string => typeof a === "string" && a.length > 0,
    );
  if (Array.isArray(from.candidates))
    to.candidates = (from.candidates as unknown[]).filter(
      (c): c is GuestMatchCandidate =>
        !!c &&
        typeof c === "object" &&
        typeof (c as { guestId?: unknown }).guestId === "string",
    );
}

/**
 * Re-throw a handler's {@link ServerError} as a plain Error whose `.message`
 * is the i18n code, KEEPING every rich field ({@link RichErrorFields})
 * attached. Use this instead of `throw new Error(err.message)` — that
 * pattern silently strips the raw backend text and generic codes degrade to
 * a bare "Validation error". `handleErrorServer` restores the attached
 * fields from a caught Error, so the round-trip is lossless.
 */
/**
 * The i18n key under `errors.*` that explains a failed call, or `"unknown"`.
 *
 * The backend answers with a precise code — a wallet template without an icon
 * is rejected as `walletIconRequired`, and there is copy for that in every
 * locale. Showing it turns "please try again" into "upload an icon first",
 * which is the difference between advice that works and advice that cannot.
 *
 * The caller passes its own `t.has`, because whether copy EXISTS is the whole
 * question: without this check a new backend code would be rendered at the
 * operator as a raw identifier.
 *
 * **Both shapes, because the codebase has two.** `@/lib/api/handlers/*` CATCH
 * and hand back a `ServerError`; `@/lib/api/clients/*` THROW, and `rethrowApi`
 * puts the very same backend code on `Error.message`. A first version of this
 * helper accepted only the first shape, which made its use at a client call
 * site a no-op — every rejection fell through to the `catch` and kept saying
 * "please try again", which is the sentence it was written to replace.
 *
 * **`hasKey` alone is NOT a code-vs-prose filter**, which an earlier version of
 * this comment claimed it was. Two measured holes, both with `t.has` over the
 * real `errors` namespace:
 *
 * - it walks the prototype chain, so `t.has("toString")` is `true` and
 *   `t("toString")` renders the literal string `"errors.toString"`;
 * - the namespace also holds UI chrome (`close`, `retry`, `error`) and 24
 *   ICU-parameterised keys, so a colliding code renders as `"Close"` — a
 *   confident, wrong diagnosis — or as `"errors.afvRequired"` when the
 *   placeholders go unfilled.
 *
 * So the shape is checked too, against the rule the backend's own camelCase
 * pass-through uses. Prose ("Network Error"), dotted paths and SCREAMING_CASE
 * sentinels are out.
 *
 * That is NOT the same as "precisely the set that can arrive", which an earlier
 * version of this said: `resolveErrorCode` has a `WorkerRpcException` arm ahead
 * of the pass-through that returns snake_case keys such as `worker_rpc_error`.
 * Harmless today — that copy lives at the top level of the message files, not
 * under `errors.*`, so it resolved to "unknown" before this guard too — but the
 * set is open, and the day such a key moves under `errors.*` this guard would
 * swallow it silently.
 *
 * What remains, stated rather than papered over: a backend code that happens to
 * equal one of those chrome or ICU key names would still surface. Neither is
 * reachable from the two call sites here (attachment create), and closing it
 * properly means a dedicated `errors.backend.*` namespace — a migration, not a
 * line in a wallet fix.
 *
 * Lives here rather than in a component because it had two call sites and only
 * one of them got it right.
 */
export function serverErrorKey(
  result: unknown,
  hasKey: (key: string) => boolean,
): string {
  const raw = isServerErr(result)
    ? result.message
    : result instanceof Error
      ? result.message
      : undefined;
  if (!raw || !isBackendErrorCode(raw)) return "unknown";
  return hasKey(raw) ? raw : "unknown";
}

/**
 * Could this string be a code the backend emitted, as opposed to prose, a
 * sentinel, or an inherited `Object.prototype` member?
 *
 * The pattern is not a guess: `ErrorFormater.resolveErrorCode` passes an
 * exception message through verbatim exactly when it matches `^[a-z][a-zA-Z0-9]*$`,
 * so this accepts precisely the set that can arrive as an `errorCode`.
 */
export function isBackendErrorCode(candidate: string): boolean {
  if (!/^[a-z][a-zA-Z0-9]*$/.test(candidate)) return false;
  // `toString`, `valueOf`, `constructor`, `hasOwnProperty` … all match the
  // pattern above AND satisfy next-intl's `t.has`, because it reads through the
  // prototype chain. Rendering one gives the operator the raw key path.
  return !Object.prototype.hasOwnProperty.call(Object.prototype, candidate);
}

/**
 * Errors re-thrown from a {@link ServerError}. That value was made by
 * `handleErrorServer`, which already reported it if it was ours — now that the
 * thrown Error carries the status, a second `handleErrorServer` further up
 * would otherwise file the same 5xx again.
 */
const alreadyReported = new WeakSet<object>();

export function throwServerError(err: ServerError): never {
  const e = new Error(err.message) as Error & RichErrorFields & { status?: number };
  copyRichFields(err, e);
  // `status` as well as `httpStatus`: the query retry brake, `handleError` and
  // `reportIfOurs` read the axios-style name, and before this a handler path
  // reached none of them with a status at all.
  if (typeof err.httpStatus === "number") e.status = err.httpStatus;
  alreadyReported.add(e);
  throw e;
}

/**
 * A handler result as a value, or the refusal as a throw.
 *
 * Handlers RETURN a {@link ServerError} instead of throwing; a react-query
 * `queryFn`, a `mutationFn` or a `try` block wants the throw. This is that
 * bridge, so a call site does not hand-roll `if (isServerErr(r)) throw …` and
 * lose the status or the rich fields on the way (throwServerError keeps both).
 *
 * Only where something catches it — try/catch, a queryFn/mutationFn, or
 * `useActionError().run`. Anywhere else an ordinary business refusal
 * (`activeSubscription`, `eventPlanLocked`) becomes an unhandled rejection and
 * a Sentry event; a source guard holds that line.
 */
export function unwrap<T>(result: T | ServerError): T {
  if (isServerErr(result)) throwServerError(result);
  return result;
}

/**
 * `true` when a handler result is a success value — the readable opposite of
 * {@link isServerErr} for `if (isOk(r)) { …use r… }`. Not called `ok`: dozens
 * of call sites already have a local `const ok`.
 */
export function isOk<T>(result: T | ServerError): result is T {
  return !isServerErr(result);
}

export async function handleError(error: unknown): Promise<never> {
  if (error instanceof AuthError) throw new Error("authError");
  if (error instanceof Error) {
    const rethrown = new Error(error.message) as Error & RichErrorFields;
    // Keep the rich fields attached by rethrowApi / throwServerError alive
    // across the re-wrap (detail for translateBackendError, retryAfter for
    // rate-limit countdowns, violations for inline per-field rendering, …).
    copyRichFields(error as Error & RichErrorFields, rethrown);
    // The axios `response` is deliberately NOT carried over — it holds the raw
    // body, which is what the console redaction exists to keep out. But the
    // bare status is safe and is the one breadcrumb worth keeping: without it
    // redactErrorMeta (which reads `.response.status`) logged failures as
    // `{name:'Error'}` with no hint whether it was a 400, a 404 or a 502.
    const shaped = error as { response?: { status?: unknown }; status?: unknown };
    const status = shaped.response?.status ?? shaped.status;
    if (typeof status === "number") {
      (rethrown as Error & { status?: number }).status = status;
      rethrown.httpStatus = status;
    }
    if (alreadyReported.has(error)) alreadyReported.add(rethrown);
    throw rethrown;
  }
  throw new Error("unknown");
}

export async function handleErrorServer(error: unknown): Promise<ServerError> {
  let errorMessage = "unknown";
  const fields: RichErrorFields = {};

  if (error instanceof AuthError) {
    errorMessage = "authError";
  } else if (error instanceof Error) {
    // AxiosError carries the backend payload at error.response.data — the i18n
    // key lives in `errorCode`. Prefer it over the raw axios .message
    // ("Request failed with status code 401").
    const axiosShape = error as Error & {
      response?: { status?: unknown; data?: { errorCode?: unknown; message?: unknown } };
      status?: unknown;
    };
    const data = axiosShape.response?.data;
    let dataDetail: string | undefined;
    if (data && typeof data.errorCode === "string" && data.errorCode.length > 0) {
      errorMessage = data.errorCode;
      // Backend sends BOTH the i18n key and the raw message — keep the raw
      // text so generic codes (validationError) still name the field.
      if (
        typeof data.message === "string" &&
        data.message.length > 0 &&
        data.message !== errorMessage
      ) {
        dataDetail = data.message;
      }
    } else if (data && typeof data.message === "string" && data.message.length > 0) {
      errorMessage = data.message;
    } else {
      errorMessage = error.message;
    }
    // Rich fields, one extraction rule (copyRichFields): first from the
    // fields rethrowApi / throwServerError attached to the Error itself (the
    // fallback when the axios response is no longer reachable — a full
    // ServerError round-trips through handleError re-wraps), then from the
    // backend envelope, which wins where both carry a value.
    copyRichFields(error as Error & RichErrorFields, fields);
    copyRichFields((data ?? {}) as RichErrorFields, fields);
    // `detail` on the envelope lives in `data.message` (not `data.detail`) —
    // apply it last so the envelope's raw text wins over any stale own-field.
    if (dataDetail !== undefined) fields.detail = dataDetail;
    // The HTTP status, which this function used to read only for Sentry and
    // then drop: the live response first, then the `status` a client re-wrap
    // (rethrowApi / handleError / throwServerError) left on the Error. A
    // network failure has neither and keeps no status — that absence is
    // itself the signal ("never reached a server").
    const status = axiosShape.response?.status ?? axiosShape.status;
    if (typeof status === "number") fields.httpStatus = status;
  }

  // An empty id list carries no information — omit the key entirely, like
  // the pre-refactor code did.
  if (fields.missingContactIds && fields.missingContactIds.length === 0)
    delete fields.missingContactIds;
  if (fields.suppressedRecipients && fields.suppressedRecipients.length === 0)
    delete fields.suppressedRecipients;

  reportIfOurs(error, errorMessage);

  return { message: errorMessage, isServerError: true, ...fields };
}

/**
 * {@link handleErrorServer} that keeps the HTTP status as `httpStatus` — for
 * callers that tell "this route does not exist (yet)" (404/405 after a backend
 * rollback) from a refusal, like the member invite page.
 */
export async function handleErrorServerWithStatus(error: unknown): Promise<ServerError> {
  const serverError = await handleErrorServer(error);
  const shaped = error as { status?: unknown; response?: { status?: unknown } } | null;
  const status = shaped?.response?.status ?? shaped?.status;
  return typeof status === "number" ? { ...serverError, httpStatus: status } : serverError;
}

/**
 * Handled is not the same as harmless.
 *
 * Every API failure that reaches here becomes a VALUE (`{isServerError: true}`)
 * rather than a rejection, so react-query records the query as a SUCCESS and
 * Sentry's ErrorBoundary — the app's only reporting path — never sees it. On
 * 2026-09-12 a backend lane returned `400 {"errorCode":"unknown"}` for every
 * row of an event's sent-mails table, all day, with zero Sentry issues; it was
 * found by a person opening devtools.
 *
 * Reported: any 5xx, and any response whose code is `unknown` — the backend
 * saying it could not name its own failure is exactly the shape that outage
 * had. Ordinary user-facing 4xx (validation, permissions, not-found) stay
 * quiet: they are the product working, and drowning the stream in them is how
 * a real signal gets ignored.
 */
function reportIfOurs(error: unknown, errorCode: string): void {
  if (typeof error === "object" && error !== null && alreadyReported.has(error)) return;
  const shaped = error as {
    response?: { status?: number; data?: { message?: unknown } };
    /** `handleError` re-wraps drop `.response` and copy the bare status here … */
    status?: number;
    /** … and the envelope's prose here (`rethrowApi` sets it). */
    detail?: unknown;
  };
  const status = shaped?.response?.status ?? shaped?.status;
  if (typeof status !== "number") {
    // No status means the request never reached a server — an empty body that
    // a client turned into `new Error("unknown")` (61 of them do), an aborted
    // request, an offline tab. Those carry a useless stack and would be
    // indistinguishable from the outage this reporting exists to catch.
    return;
  }
  // `errorCode === "unknown"` alone is NOT the outage shape: the backend's
  // `ErrorFormater.resolveErrorCode` answers `unknown` for any prose message
  // it has no code for, and ~214 legacy `formatError(e.getMessage())` lanes
  // still produce exactly that for ordinary user-facing 400s ("Guest not
  // found", validation prose). Reporting those would bury the stream this
  // exists to make readable. What set the outage apart was an `unknown` with
  // NO message at all — a native-image NPE the backend could not describe.
  // The re-wrap lane carries the envelope's message as `detail`; without
  // reading it, every legacy prose 400 that went through `handleError`
  // (EventClient, BillingClient, CompanyHandling — 24 call sites) reported
  // as nameless: exactly the flood this rule exists to prevent.
  const envelopeMessage = shaped?.response?.data?.message ?? shaped?.detail;
  const nameless = typeof envelopeMessage !== "string" || envelopeMessage.trim().length === 0;
  const worthReporting = status >= 500 || (errorCode === "unknown" && nameless);
  if (!worthReporting) return;
  // The billing checkout pause (main-backend `@BillingCheckoutGate`, Stripe
  // go-live window) answers EVERY subscribe / purchase / coupon / cancel click
  // with 503 `billingServiceUnavailable`. That is the switch working, not an
  // outage; one event per blocked click would bury the window's real alerts.
  // The backend logs each 503 itself.
  if (errorCode === "billingServiceUnavailable") return;
  // Billing-comms Teil 2b: the self-service switch (`runtime_flags/
  // billingSelfService` off for this company) answers setup-intent / confirm /
  // pay / sync with 503 `billingSelfServiceDisabled` — same reasoning. The
  // other 2b 5xx codes (`paymentMethodSyncPartial`, `paymentStatusUnknown`)
  // are anomalies and stay reported.
  if (errorCode === "billingSelfServiceDisabled") return;
  try {
    Sentry.captureException(error, {
      // Tags are INDEXED and searchable, and `sentryBeforeSend` does not scrub
      // them. `errorCode` is backend prose whenever the envelope carried a
      // `message` and no `errorCode`, so only a real code is allowed through.
      tags: { handled: "api", errorCode: isBackendErrorCode(errorCode) ? errorCode : "raw" },
      extra: { status },
    });
  } catch {
    // Reporting must never be the thing that breaks a request.
  }
}

/**
 * The create flow's 409: the event is full and the admin must choose
 * (waitlist / add over cap / cancel). Carries `capacityContext` for the popup.
 */
/**
 * Is this handler result the error envelope, rather than a real response?
 *
 * The handlers RETURN `ServerError` instead of throwing (see `handleErrorServer`),
 * so every caller has to sort the two apart itself — and every caller wrote the
 * same four-line check by hand. Two of them left out the `!== null` arm, which
 * held only for as long as the endpoint answered with no body at all:
 * `typeof null === "object"`, so `"isServerError" in null` throws a TypeError
 * from the middle of a save handler the day the response carries a JSON null.
 *
 * One narrowing, used everywhere, so that arm cannot go missing again.
 */
export function isServerErrorResponse(
  value: unknown,
): value is ServerError {
  return (
    typeof value === "object" &&
    value !== null &&
    "isServerError" in value &&
    (value as { isServerError?: unknown }).isServerError === true
  );
}

/**
 * The lifecycle triggers the batch update reports it ENQUEUED for one guest.
 *
 * Empty is a real, meaningful answer: it means the write announced nothing,
 * which is the normal case and — after a ticked "run the automations again" —
 * the one case the admin most needs to be told about. An absent body means the
 * same thing, so both collapse to `[]` here rather than to "unknown".
 *
 * Deliberately tolerant of shape: this reads a response that may predate the
 * field entirely (a backend rollback, a cached deployment), and a cockpit that
 * threw on that would be worse than one that reports nothing announced.
 */
export function enqueuedTriggersFor(
  response: unknown,
  guestId: string,
): string[] {
  if (typeof response !== "object" || response === null) return [];
  const map = (response as { lifecycleTriggers?: unknown }).lifecycleTriggers;
  if (typeof map !== "object" || map === null) return [];
  const forGuest = (map as Record<string, unknown>)[guestId];
  return Array.isArray(forGuest) ? forGuest.filter((t): t is string => typeof t === "string") : [];
}

export function isCapacityDecision(err: ServerError | null | undefined): boolean {
  return !!err && err.isServerError && err.message === "capacityDecisionRequired";
}

/**
 * Pull the capacity snapshot for the popup from either the create flow's
 * top-level envelope or the first capacity-blocked per-guest failure.
 */
export function capacityContextOf(
  err: ServerError | null | undefined
): CapacityContext | undefined {
  if (!err || !err.isServerError) return undefined;
  if (err.capacityContext) return err.capacityContext;
  return err.failures?.find((f) => f.capacityContext)?.capacityContext;
}

/**
 * Per-guest failures from a batch-update that were blocked by the registration
 * rules (event at capacity) on an approval / waitlist promotion. The guestlist
 * surfaces these as a "promote anyway?" confirmation and re-sends with
 * `overrideRules: true`.
 */
export function approvalBlockedGuestIds(err: ServerError | null | undefined): string[] {
  if (!err || !err.isServerError || !err.failures) return [];
  return err.failures
    .filter((f) => f.errorCode === "approvalBlockedByRules")
    .map((f) => f.guestId);
}

/**
 * Pattern recognizer for the well-known authz error codes produced by the
 * backend CUSTOM-role feature. Callers can branch on these to surface a
 * different UI than the generic <SError />:
 *
 *  - {@link isInsufficientCapability}: 403 from CapabilityInterceptor. Show
 *    AccessDeniedWithSuggestion (the user's CUSTOM role is missing a cap).
 *  - {@link isRoleInactive}: 403 / 401 when the EventRole was demoted from
 *    CUSTOM or the bound template was deleted. Reroute to "/" + toast.
 *  - {@link isFieldNotAllowed}: 403 from AuthzWriteValidator. Show the
 *    offending field names parsed from the suffix.
 *  - {@link isUnknownCapability}: 400 from template create/update when the
 *    request body contains caps not in Capability.ALL. Indicates frontend
 *    cap catalogue drift.
 */
export function isInsufficientCapability(err: ServerError | null | undefined): boolean {
  return !!err && err.isServerError && err.message === "insufficientCapability";
}

export function isRoleInactive(err: ServerError | null | undefined): boolean {
  return !!err && err.isServerError && err.message === "ROLE_INACTIVE";
}

export function isFieldNotAllowed(err: ServerError | null | undefined): boolean {
  return !!err && err.isServerError && err.message.startsWith("fieldNotAllowed");
}

export function parseFieldNotAllowed(err: ServerError): string[] {
  if (!isFieldNotAllowed(err)) return [];
  const idx = err.message.indexOf(":");
  if (idx === -1) return [];
  return err.message
    .slice(idx + 1)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function isUnknownCapability(err: ServerError | null | undefined): boolean {
  return !!err && err.isServerError && err.message.startsWith("unknownCapability");
}

/**
 * 403 from a CUSTOM-role write where the submitted guest body would
 * not satisfy the template's row filter — i.e. the new row would be
 * invisible to its own creator. Surfaced by createGuests / createPending
 * / createCompanionRequest. The frontend should show a concrete
 * "this guest would be invisible to your role" message and a hint to
 * adjust the row filter or ask an admin to widen the template, NOT a
 * generic insufficientCapability banner.
 */
export function isGuestOutsideScope(err: ServerError | null | undefined): boolean {
  return !!err && err.isServerError && err.message === "guestOutsideScope";
}

/**
 * 400 partial-failure entry from a CUSTOM-role batch update where one
 * or more guestIds fall outside the template's row filter. Surfaced
 * per-id inside the BatchUpdateResult.failed[].errorCode field — NOT
 * a top-level error envelope. Use this when iterating per-id results
 * to show "this guest is outside your role's scope; the rest of the
 * batch succeeded" on the affected rows.
 */
export function isGuestIdNotInScope(err: ServerError | null | undefined): boolean {
  return !!err && err.isServerError && err.message === "guestIdNotInScope";
}
